import { useRef, useCallback, useEffect, useState } from 'react';

export interface UseModalGesturesProps {
    onClose: () => void;
    isVisible: boolean;
}

export function useModalGestures({ onClose, isVisible }: UseModalGesturesProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [startY, setStartY] = useState(0);
    const [currentY, setCurrentY] = useState(0);
    const sheetRef = useRef<HTMLDivElement>(null);

    // Handle close with animation and restore body scroll
    const handleClose = useCallback(() => {
        // Restore body scroll immediately when closing
        document.body.style.overflow = 'unset';
        setTimeout(onClose, 300); // Wait for animation to complete
    }, [onClose]);

    // Cleanup function for drag events
    const finishDrag = useCallback(() => {
        const deltaY = currentY - startY;
        const threshold = 50; // Reduced threshold for easier closing

        if (deltaY > threshold) {
            handleClose();
        } else {
            // Snap back
            if (sheetRef.current) {
                sheetRef.current.style.transform = 'translateY(0)';
                sheetRef.current.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
            }
        }

        setIsDragging(false);
        setStartY(0);
        setCurrentY(0);
    }, [currentY, startY, handleClose]);

    // Global mouse event handlers
    const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging) return;

        const clientY = e.clientY;
        const deltaY = clientY - startY;

        // Only allow downward drag
        if (deltaY > 0) {
            setCurrentY(clientY);
            if (sheetRef.current) {
                sheetRef.current.style.transform = `translateY(${deltaY}px)`;
                sheetRef.current.style.transition = 'none';
            }
        }
    }, [isDragging, startY]);

    const handleGlobalMouseUp = useCallback(() => {
        if (!isDragging) return;

        // Remove global listeners
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);

        finishDrag();
    }, [isDragging, handleGlobalMouseMove, finishDrag]);

    // Touch/mouse handlers for swipe to close
    const handleDragStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        setStartY(clientY);
        setCurrentY(clientY);
        setIsDragging(true);

        // For mouse events, add global listeners
        if ('clientY' in e) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
            e.preventDefault(); // Prevent text selection
        }

        e.stopPropagation();
    }, [handleGlobalMouseMove, handleGlobalMouseUp]);

    const handleDragMove = useCallback((e: React.TouchEvent) => {
        if (!isDragging) return;

        const clientY = e.touches[0].clientY;
        const deltaY = clientY - startY;

        // Only allow downward drag
        if (deltaY > 0) {
            setCurrentY(clientY);
            if (sheetRef.current) {
                sheetRef.current.style.transform = `translateY(${deltaY}px)`;
                sheetRef.current.style.transition = 'none';
            }
        }

        e.preventDefault();
        e.stopPropagation();
    }, [isDragging, startY]);

    const handleDragEnd = useCallback(() => {
        if (!isDragging) return;
        finishDrag();
    }, [isDragging, finishDrag]);

    // Click outside to close
    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        // Make sure we're clicking the backdrop itself, not a child element
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    // Handle swipe down from top to close
    const handleTopSwipeDown = useCallback((e: TouchEvent) => {
        const touch = e.touches[0];
        const startY = touch.clientY;

        // Only handle swipes that start from the top 50px of the screen
        if (startY <= 50) {
            const handleTouchMove = (moveEvent: TouchEvent) => {
                const currentTouch = moveEvent.touches[0];
                const deltaY = currentTouch.clientY - startY;

                // If swiped down more than 100px, close the modal
                if (deltaY > 100) {
                    handleClose();
                    document.removeEventListener('touchmove', handleTouchMove);
                }
            };

            document.addEventListener('touchmove', handleTouchMove, { passive: true });

            // Clean up listener after a short time
            setTimeout(() => {
                document.removeEventListener('touchmove', handleTouchMove);
            }, 1000);
        }
    }, [handleClose]);

    // Setup body scroll prevention and cleanup
    useEffect(() => {
        if (isVisible) {
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            // Restore body scroll when modal is closed
            document.body.style.overflow = 'unset';
            // Clean up any remaining global event listeners
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [isVisible, handleGlobalMouseMove, handleGlobalMouseUp]);

    // Add swipe down from top listener
    useEffect(() => {
        document.addEventListener('touchstart', handleTopSwipeDown, { passive: true });

        return () => {
            document.removeEventListener('touchstart', handleTopSwipeDown);
        };
    }, [handleTopSwipeDown]);

    return {
        sheetRef,
        isDragging,
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        handleBackdropClick,
        handleClose
    };
}
